import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const EASE = [0.16, 1, 0.3, 1];

const GROUPS = [
  {
    id: "lead-times",
    label: "01 / Lead times & process",
    items: [
      {
        q: "How fast can you turn an order around?",
        a: "Once you approve the proof, most runs print and ship within 2–5 working days from the local print hub in your destination country. For full tours, send us your routing at least 3 weeks before the first date so stock is waiting at the venue, not chasing it.",
      },
      {
        q: "When should we send our tour routing?",
        a: "Three to four weeks before the first show is ideal — that leaves room for design, proofing, production and delivery buffers. Shorter notice is often still possible; tell us the dates and we'll tell you honestly what's achievable.",
      },
      {
        q: "Can you deliver mid-tour, or top us up between dates?",
        a: "Yes. Because we print on demand in each region, we can run reprints or top-ups to an upcoming venue, hotel or promoter office while you're on the road. You never have to over-order at the start.",
      },
      {
        q: "Do you hold stock of our merch?",
        a: "No warehouses, no dead stock. Every run is printed on demand for a specific leg or venue. Reorders are a message away since your artwork and specs stay on file.",
      },
    ],
  },
  {
    id: "sizing",
    label: "02 / Sizing runs & quantities",
    items: [
      {
        q: "What are your minimum order quantities?",
        a: "Minimums are intentionally low for touring acts: 25 for tees, 20 for hoodies, 24 for caps and beanies, and 50 for posters and totes. Above that, order exactly what each venue justifies — no forced volume.",
      },
      {
        q: "Can we set our own size split?",
        a: "Yes. Full size curves from XS to 3XL on most garments, and you set the split per size per run. If you're unsure, we'll suggest a curve based on your venues and audience.",
      },
      {
        q: "Can we mix product types in one delivery?",
        a: "Absolutely. Tees, hoodies, hats, posters and totes can be consolidated into a single delivery per destination, so the merch table is stocked from one box, not five couriers.",
      },
      {
        q: "Can we get a sample before committing to a run?",
        a: "Yes — we can ship a blank or printed sample of your chosen garment so you can check fit, weight and print quality before the full run goes ahead.",
      },
    ],
  },
  {
    id: "artwork",
    label: "03 / Artwork & design",
    items: [
      {
        q: "What artwork files do you need from us?",
        a: "Vector files are best — AI, EPS or print-ready PDF. High-resolution PNG at 300dpi at final print size also works. Send what you have; we handle separations, colour matching and print prep.",
      },
      {
        q: "We don't have a designer. Can you create the artwork?",
        a: "Yes, that's a core part of what we do. Our merch design team can create tour designs from scratch or adapt your existing artwork for print. Nothing is printed until you've approved the final proof.",
      },
      {
        q: "How many print colours can we use?",
        a: "For screenprinting we typically run up to 4 spot colours per design, which keeps unit costs sensible at merch-table prices. DTG handles full-colour and photographic prints with no colour limit, and embroidery is digitised to match your artwork.",
      },
      {
        q: "Can you match our existing merch so old and new stock look the same?",
        a: "Yes. Send a photo or a physical sample of your current run and we'll match garment, ink and placement as closely as the print method allows.",
      },
    ],
  },
  {
    id: "shipping",
    label: "04 / International & domestic shipping",
    items: [
      {
        q: "How do you ship internationally without customs delays?",
        a: "We don't ship your merch across borders — we print it there. Your order is produced by a print-on-demand partner inside the destination country and shipped as a domestic consignment. No customs declarations, no import duty, no cargo fees, no lost luggage.",
      },
      {
        q: "Which countries can you deliver to?",
        a: "We route through print hubs covering the US, UK, EU, Australia and Japan, and can quote most other tour destinations on request. If a band can tour there, we can almost always get merch there.",
      },
      {
        q: "Where exactly do you deliver? The venue or us?",
        a: "Whichever suits the run: the venue's green room or production office, your hotel, a promoter's address, or directly to the band for domestic orders. Deliveries are timed to land before soundcheck, not during it.",
      },
      {
        q: "Do we get tracking, and what does shipping cost?",
        a: "Every consignment ships tracked, and tracking is shared with you as soon as it moves. Shipping is included in your quote per run — no surprise surcharges afterwards.",
      },
    ],
  },
];

export default function FaqPage() {
  return (
    <div data-testid="faq-page" className="pt-16">
      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="mb-16 max-w-2xl"
        >
          <p className="mb-4 font-mono text-xs uppercase tracking-[0.25em] text-zinc-500">FAQ</p>
          <h1 className="font-display text-4xl font-extrabold tracking-tighter sm:text-5xl lg:text-6xl">
            Answers before <span className="font-serif italic font-medium">you ask.</span>
          </h1>
          <p className="mt-5 text-sm leading-relaxed text-zinc-600 sm:text-base">
            Lead times, size runs, artwork and how merch crosses borders without you carrying it.
            Anything else — send an enquiry and you'll get a straight answer within 24 hours.
          </p>
        </motion.div>

        <div className="space-y-14">
          {GROUPS.map((group, gi) => (
            <motion.div
              key={group.id}
              data-testid={`faq-group-${group.id}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: gi * 0.08, ease: EASE }}
              className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_2fr]"
            >
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-500 lg:pt-2">
                {group.label}
              </p>
              <Accordion type="single" collapsible className="w-full">
                {group.items.map((item, ii) => (
                  <AccordionItem
                    key={ii}
                    value={`${group.id}-${ii}`}
                    className="border-b border-zinc-200"
                  >
                    <AccordionTrigger
                      data-testid={`faq-question-${group.id}-${ii}`}
                      className="py-5 text-left font-display text-base font-bold tracking-tight hover:text-tour-red hover:no-underline sm:text-lg"
                    >
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent
                      data-testid={`faq-answer-${group.id}-${ii}`}
                      className="pb-6 text-sm leading-relaxed text-zinc-600"
                    >
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: EASE }}
          className="mt-20 flex flex-col items-start justify-between gap-6 border border-zinc-200 bg-white p-8 sm:flex-row sm:items-center sm:p-10"
        >
          <div>
            <h2 className="font-display text-xl font-bold tracking-tight sm:text-2xl">Still got a question?</h2>
            <p className="mt-2 text-sm text-zinc-600">Send the details of your run and we'll come back within 24 hours.</p>
          </div>
          <Link
            data-testid="faq-enquiry-cta"
            to="/enquiry"
            className="group flex items-center gap-2 bg-tour-void px-7 py-4 font-mono text-xs uppercase tracking-widest text-white transition-colors hover:bg-tour-red"
          >
            Start an enquiry
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </motion.div>
      </section>
    </div>
  );
}
